import * as React from 'react';

import {
  Accordion,
  Col,
  formatMoney,
  humanReadableShortPricingUnit,
  LocalizedDate,
  Row,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import {
  LucideCalendar,
  LucideDollarSign,
  LucideMapPin,
  LucideTag,
  LucideUsers,
} from 'lucide-react';

import {
  $cloneDialogSelectedAccountIds,
  $remoteCloneDialogSelectedAccounts,
} from '../../_state/quoteClone.state';
import { useTranslations } from '../../i18n/useTranslations';
import { QuoteDetailRecord } from '../../types/quote';

interface QuoteCloneWizardStepSummaryProps {
  quote: QuoteDetailRecord;
}

//
// ⚛️ ⚛️ ⚛️ QUOTE CLONE WIZARD STEP SUMMARY ⚛️ ⚛️ ⚛️
//
export const QuoteCloneWizardStepSummary: React.FC<
  QuoteCloneWizardStepSummaryProps
> = ({ quote }) => {
  const t = useTranslations();

  // Atoms
  const selectedIds = useAtomValue($cloneDialogSelectedAccountIds);
  const { data: accounts } = useAtomValue($remoteCloneDialogSelectedAccounts);

  // Memoized values
  const selectedAccountNames = React.useMemo(() => {
    if (!accounts) return [];
    return accounts
      .filter((a) => selectedIds.includes(a.id))
      .map((a) => a.name);
  }, [accounts, selectedIds]);

  return (
    <Col gap6 className="@QuoteCloneWizardStepSummary">
      <div className="rounded-lg border border-neutral/10 bg-neutral/5 p-4">
        <p className="text-sm text-neutral/80">
          {renderBoldText(t.quotes.clone.summaryBanner(selectedIds.length))}
        </p>
      </div>

      <Col gap4>
        <SummaryRow
          icon={<LucideMapPin className="size-5 text-neutral/50" />}
          label={t.quotes.clone.summaryFields.facility}
          value={quote.facility?.facilityName ?? t.quotes.clone.noFacility}
        />
        <SummaryRow
          icon={<LucideCalendar className="size-5 text-neutral/50" />}
          label={t.quotes.clone.summaryFields.deadline}
          value={
            quote.validUntil ? (
              <LocalizedDate date={quote.validUntil} />
            ) : (
              t.quotes.clone.noDeadline
            )
          }
        />
        <SummaryRow
          icon={<LucideDollarSign className="size-5 text-neutral/50" />}
          label={t.quotes.clone.summaryFields.totalValue}
          value={formatMoney(
            quote.subtotal.amount,
            quote.subtotal.currencyCode,
          )}
        />

        <Accordion
          variant="ghost"
          size="sm"
          defaultExpanded
          trigger={
            <Row gap3>
              <div className="flex-shrink-0 rounded-sm bg-neutral/5 p-2.5">
                <LucideTag className="size-5 text-neutral/50" />
              </div>
              <Col className="text-left">
                <span className="text-xs font-normal text-neutral/50">
                  {t.quotes.clone.summaryFields.materials}
                </span>
                <span className="text-sm font-semibold">
                  {t.quotes.clone.summaryFields.materialsCount(
                    quote.lineItems.length,
                  )}
                </span>
              </Col>
            </Row>
          }
        >
          <Col className="ml-12">
            {quote.lineItems.map((li) => (
              <Row
                key={li.id}
                yStart
                xBetween
                className="border-b border-dashed border-neutral/10 py-3 last:border-0"
              >
                <Col>
                  <span className="text-sm font-semibold">
                    {li.item?.name ?? t.quotes.clone.unknownItem}
                  </span>
                  {li.packageFormat && (
                    <span className="text-xs text-neutral/50">
                      {li.packageFormat}
                    </span>
                  )}
                </Col>
                <Col xEnd>
                  <span className="text-sm font-semibold">
                    {formatMoney(
                      li.totalPrice.amount,
                      li.totalPrice.currencyCode,
                    )}
                  </span>
                  <span className="text-xs text-neutral/50">
                    {li.quantity.toLocaleString()}{' '}
                    {humanReadableShortPricingUnit(li.unit)}
                  </span>
                </Col>
              </Row>
            ))}
          </Col>
        </Accordion>

        <Accordion
          variant="ghost"
          size="sm"
          defaultExpanded
          trigger={
            <Row gap3>
              <div className="flex-shrink-0 rounded-sm bg-neutral/5 p-2.5">
                <LucideUsers className="size-5 text-neutral/50" />
              </div>
              <Col className="text-left">
                <span className="text-xs font-normal text-neutral/50">
                  {t.quotes.clone.summaryFields.customers}
                </span>
                <span className="text-sm font-semibold">
                  {selectedAccountNames.length}
                </span>
              </Col>
            </Row>
          }
        >
          <Col className="ml-12">
            {selectedAccountNames.map((name) => (
              <div
                key={name}
                className="border-b border-dashed border-neutral/10 py-3 text-sm last:border-0"
              >
                {name}
              </div>
            ))}
          </Col>
        </Accordion>
      </Col>
    </Col>
  );
};

//
// ⚛️ ⚛️ ⚛️ HELPERS ⚛️ ⚛️ ⚛️
//
function renderBoldText(text: string): React.ReactNode {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
  );
}

const SummaryRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Row gap3 yStart className="@SummaryRow">
    <div className="mt-0.5 flex-shrink-0 rounded-sm bg-neutral/5 p-2.5">
      {icon}
    </div>
    <Col>
      <span className="text-xs text-neutral/50">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </Col>
  </Row>
);
